import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEmpresa } from '../contexts/EmpresaContext';
import { PeriodoProvider } from '../contexts/PeriodoContext';
import { Button } from '../components/ui/button';
import ChatWidget from '../components/ChatWidget';
import NotificacoesDropdown from '../components/ui/NotificacoesDropdown';
import SeletorPeriodo from '../components/SeletorPeriodo';
import EmpresaSelector from '../components/EmpresaSelector';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Clock, Users, LayoutDashboard, Settings, LogOut, User, 
  ChevronDown, ChevronRight, Menu, X, Bell, Calendar, Timer, FileText,
  AlertTriangle, Activity, FileCheck, Stethoscope, BarChart3,
  RefreshCw, PieChart, Upload, Percent, ClipboardList, FileBarChart,
  Briefcase, HeartPulse, FolderOpen, CalendarClock
} from 'lucide-react';

const EmpresaLayout = () => {
  const { user, logout, token } = useAuth();
  const { isUsuarioEscritorio } = useEmpresa();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Estado para controlar qual grupo está expandido (apenas um por vez)
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Encontrar o grupo ativo baseado na rota atual
  useEffect(() => {
    menuGroups.forEach((group, index) => {
      if (group.title && group.items.some(item => isActive(item.path))) {
        setExpandedGroup(index);
      }
    });
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Accordion: ao clicar em um grupo, fecha o anterior e abre o novo
  const toggleGroup = (index) => {
    setExpandedGroup(prev => prev === index ? null : index);
  };

  // Menu reorganizado conforme solicitado
  const menuGroups = [
    {
      // Dashboard - item único sempre visível
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/empresa' },
      ]
    },
    {
      title: 'Cadastros',
      icon: FolderOpen,
      items: [
        { icon: Users, label: 'Empregados', path: '/empresa/empregados' },
        { icon: User, label: 'Líderes', path: '/empresa/lideres' },
        { icon: Percent, label: 'Config. Eventos', path: '/empresa/horas-extras' },
      ]
    },
    {
      title: 'Jornadas',
      icon: CalendarClock,
      items: [
        { icon: Clock, label: 'Horários', path: '/empresa/horarios' },
        { icon: Calendar, label: 'Escalas', path: '/empresa/escalas-trabalho' },
        { icon: Users, label: 'Vínculos', path: '/empresa/escalas-ciclicas?tab=vinculos' },
      ]
    },
    {
      title: 'Apontamentos',
      icon: ClipboardList,
      items: [
        { icon: Upload, label: 'Importar AFD', path: '/empresa/importar-afd' },
        { icon: Clock, label: 'Marcações', path: '/empresa/marcacoes' },
        { icon: FileText, label: 'Ocorrências', path: '/empresa/ocorrencias' },
        { icon: Timer, label: 'Banco de Horas', path: '/empresa/banco-horas' },
      ]
    },
    {
      title: 'Epidemiologia',
      icon: HeartPulse,
      items: [
        { icon: BarChart3, label: 'Painel', path: '/empresa/epidemiologia' },
        { icon: Stethoscope, label: 'Atestados', path: '/empresa/atestados' },
        { icon: Activity, label: 'NTEP', path: '/empresa/ntep' },
        { icon: AlertTriangle, label: 'Riscos', path: '/empresa/riscos' },
      ]
    },
    {
      title: 'Relatórios',
      icon: FileBarChart,
      items: [
        { icon: FileCheck, label: 'Cartão de Ponto', path: '/empresa/cartao-ponto' },
        { icon: Bell, label: 'Notificações', path: '/empresa/notificacoes' },
      ]
    },
    {
      // Configurações - item único sempre visível
      items: [
        { icon: Settings, label: 'Configurações', path: '/empresa/configuracoes' },
      ]
    }
  ];

  const isActive = (path) => {
    if (path === '/empresa') return location.pathname === path;
    return location.pathname.startsWith(path.split('?')[0]);
  };

  const hasActiveItem = (group) => {
    return group.items.some(item => isActive(item.path));
  };

  const renderMenuGroup = (group, index) => {
    const isExpanded = expandedGroup === index;
    const hasActive = hasActiveItem(group);
    
    // Grupos sem título são renderizados diretamente
    if (!group.title) {
      return (
        <div key={index} className="mb-1">
          {group.items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive(item.path)
                  ? 'bg-accent text-white shadow-md'
                  : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          ))}
        </div>
      );
    }

    // Grupos com título são colapsáveis (accordion)
    return (
      <div key={index} className="mb-1">
        {/* Cabeçalho do grupo - clicável */}
        <button
          onClick={() => toggleGroup(index)}
          className={`w-full flex items-center justify-between px-3 py-2 mt-2 rounded-lg transition-all duration-200 group ${
            hasActive 
              ? 'text-white bg-slate-700/40' 
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
          }`}
        >
          <div className="flex items-center gap-2">
            {group.icon && (
              <group.icon className={`w-4 h-4 ${hasActive ? 'text-accent' : ''}`} />
            )}
            <span className="text-xs font-semibold uppercase tracking-wider">
              {group.title}
            </span>
          </div>
          <ChevronRight 
            className={`w-4 h-4 transition-transform duration-200 ${
              isExpanded ? 'rotate-90' : ''
            }`}
          />
        </button>
        
        {/* Sub-itens - com animação */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="ml-2 pl-4 border-l border-slate-700 mt-1 space-y-0.5">
            {group.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-accent text-white shadow-md'
                    : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{item.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderMobileMenuGroup = (group, index) => {
    const isExpanded = expandedGroup === index;
    const hasActive = hasActiveItem(group);
    
    if (!group.title) {
      return (
        <div key={index} className="mb-1">
          {group.items.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-accent text-white'
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </div>
      );
    }

    return (
      <div key={index} className="mb-1">
        <button
          onClick={() => toggleGroup(index)}
          className={`w-full flex items-center justify-between px-3 py-2 mt-2 rounded-lg ${
            hasActive ? 'text-white bg-slate-700/40' : 'text-slate-400'
          }`}
        >
          <div className="flex items-center gap-2">
            {group.icon && <group.icon className={`w-4 h-4 ${hasActive ? 'text-accent' : ''}`} />}
            <span className="text-xs font-semibold uppercase tracking-wider">
              {group.title}
            </span>
          </div>
          <ChevronRight 
            className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
          />
        </button>
        
        <div className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-96' : 'max-h-0'
        }`}>
          <div className="ml-2 pl-4 border-l border-slate-700 mt-1">
            {group.items.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-accent text-white'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const content = (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-primary">
          {/* Logo */}
          <div className="flex items-center gap-2 h-16 px-6 border-b border-slate-700 flex-shrink-0">
            <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white font-['Outfit']">Jornyx</span>
          </div>

          {/* Navigation com scroll customizado */}
          <nav className="flex-1 py-4 px-3 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-transparent hover:scrollbar-thumb-slate-500">
            <style>{`
              nav::-webkit-scrollbar {
                width: 6px;
              }
              nav::-webkit-scrollbar-track {
                background: transparent;
              }
              nav::-webkit-scrollbar-thumb {
                background: #475569;
                border-radius: 3px;
              }
              nav::-webkit-scrollbar-thumb:hover {
                background: #64748b;
              }
            `}</style>
            {menuGroups.map((group, index) => renderMenuGroup(group, index))}
          </nav>

          {/* User - fixo no fundo */}
          <div className="p-4 border-t border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-600 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.nome}</p>
                <p className="text-xs text-slate-400 truncate capitalize">{user?.perfil?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-primary flex flex-col">
            <div className="flex items-center justify-between h-16 px-6 border-b border-slate-700 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white font-['Outfit']">Jornyx</span>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex-1 py-4 px-3 overflow-y-auto">
              {menuGroups.map((group, index) => renderMobileMenuGroup(group, index))}
            </nav>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-lg font-semibold text-slate-800 font-['Outfit']">
                Gestão de Jornada
              </h1>
            </div>

            <div className="flex items-center gap-4">
              {/* Seletor de Empresa (para usuários de escritório) */}
              {isUsuarioEscritorio && isUsuarioEscritorio() && <EmpresaSelector />}
              
              {/* Seletor de Período Global */}
              <SeletorPeriodo />
              
              <NotificacoesDropdown token={token} />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <span className="hidden sm:inline text-sm">{user?.nome}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/empresa/configuracoes')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Chat Widget - visível para usuários da empresa */}
      <ChatWidget />
    </div>
  );

  // Envolver com PeriodoProvider
  return (
    <PeriodoProvider token={token}>
      {content}
    </PeriodoProvider>
  );
};

export default EmpresaLayout;
